import { Outlet } from "react-router-dom";
import { Button } from "@/components/ui/button.jsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.jsx";
import {
  Settings as SettingsIcon,
  LogOut,
  ChevronDown,
  Building2,
  User,
  Check,
  Sun,
  Moon,
} from "lucide-react";

import { useAuth } from "@/lib/context/AuthContext.jsx";
import { useState } from "react";
import { useTheme } from "@/lib/context/ThemeContext";

export default function MainLayout() {
  return (
    <div className="min-h-screen flex w-full bg-background">
      <div className="flex-1 flex flex-col">
        <MainLayoutHeader />
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function MainLayoutHeader() {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  const handleAccountSwitch = async (accountId) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "http://localhost:8000/api"}/users/${accountId}/switch-account/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            errorData.detail ||
            "Erreur lors du changement de compte",
        );
      }

      // Hard reload the page to clear cached data
      window.location.reload();
    } catch (error) {
      console.error("Account switch failed:", error);
      // You could add a toast notification here if needed
      alert(
        error.message || "Une erreur est survenue lors du changement de compte",
      );
    }
  };

  return (
    <header className="h-16 flex items-center justify-between px-6 border-b border-border bg-card/50 backdrop-blur-sm">
      <div className="flex items-center">
        <h1
          className="text-lg font-semibold text-foreground cursor-pointer hover:text-primary transition-colors"
          onClick={() => (window.location.href = "/societes")}
        >
          App risk Luigi
        </h1>
      </div>
      <div className="flex items-center space-x-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="text-muted-foreground hover:text-foreground"
        >
          {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span className="text-sm">
                  {user?.accounts?.find((acc) => acc.active)?.name ||
                    user?.first_name + " " + user?.last_name ||
                    "Utilisateur"}
                </span>
              </div>
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            {/* Account Switching Section */}
            {user?.accounts && user.accounts.length > 0 && (
              <>
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Comptes
                </DropdownMenuLabel>
                {user.accounts.map((account) => (
                  <DropdownMenuItem
                    key={account.id}
                    onClick={() => {
                      if (!account.active) {
                        handleAccountSwitch(account.id.toString());
                      }
                    }}
                    className={`cursor-pointer ${account.active ? "bg-accent" : ""}`}
                    disabled={account.active}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        <span>{account.name}</span>
                      </div>
                      {account.active && (
                        <Check className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
              </>
            )}

            {/* Settings and Logout */}
            <DropdownMenuItem
              onClick={() => (window.location.href = "/settings")}
            >
              <SettingsIcon className="h-4 w-4 mr-2" />
              Paramètres
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} className="text-red-600">
              <LogOut className="h-4 w-4 mr-2" />
              Se déconnecter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
